import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiClient } from '../../api/client';

interface Edition {
  id: string;
  date: string;
  title: string;
  story_count: number;
  claim_count: number;
  quote_count: number;
  summary_markdown: string;
}

interface EditionsResponse {
  editions: Edition[];
}

export function WrappedPage() {
  const [editions, setEditions] = useState<Edition[]>([]);
  const [selectedEdition, setSelectedEdition] = useState<Edition | null>(null);
  const [isLoadingList, setIsLoadingList] = useState(true);

  // Fetch editions list on mount
  useEffect(() => {
    fetchEditions();
  }, []);

  const fetchEditions = async () => {
    try {
      const result = await apiClient.get<EditionsResponse>('/wrapped/editions');
      const editionsList = result.editions || [];
      setEditions(editionsList);
      // Auto-select the most recent edition
      if (editionsList.length > 0 && !selectedEdition) {
        handleSelectEdition(editionsList[0]);
      }
    } catch (err) {
      console.error('Failed to fetch editions:', err);
    } finally {
      setIsLoadingList(false);
    }
  };

  const handleSelectEdition = async (edition: Edition) => {
    // If we already have the full data, use it
    if (edition.summary_markdown) {
      setSelectedEdition(edition);
      return;
    }
    // Otherwise fetch the full edition
    try {
      const result = await apiClient.get<Edition>(`/wrapped/editions/${edition.date}`);
      setSelectedEdition(result);
    } catch (err) {
      console.error('Failed to fetch edition:', err);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Today, Wrapped</h1>
        <p className="text-gray-600 mt-1">
          AI-generated daily news summaries with themes, comparisons, and quotes.
        </p>
      </div>

      {/* Two column layout: editions list + selected edition */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Editions list */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Editions</h2>
            {isLoadingList ? (
              <div className="text-gray-500 text-sm">Loading...</div>
            ) : editions.length === 0 ? (
              <div className="text-gray-500 text-sm">No editions available yet.</div>
            ) : (
              <div className="max-h-[50vh] overflow-y-auto pr-1">
                <div className="space-y-2">
                  {editions.map((edition) => (
                    <button
                      key={edition.id}
                      onClick={() => handleSelectEdition(edition)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedEdition?.id === edition.id
                          ? 'bg-purple-100 border-purple-300 border'
                          : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                      }`}
                    >
                      <div className="font-medium text-gray-900 text-sm">
                        {formatDate(edition.date)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {edition.story_count} stories · {edition.claim_count} claims
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Selected edition content */}
        <div className="lg:col-span-3">
          {selectedEdition ? (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-200">
                <div className="text-center px-4">
                  <div className="text-2xl font-bold text-purple-600">{selectedEdition.story_count}</div>
                  <div className="text-sm text-gray-500">Stories</div>
                </div>
                <div className="text-center px-4 border-l border-gray-200">
                  <div className="text-2xl font-bold text-indigo-600">{selectedEdition.claim_count}</div>
                  <div className="text-sm text-gray-500">Claims</div>
                </div>
                <div className="text-center px-4 border-l border-gray-200">
                  <div className="text-2xl font-bold text-blue-600">{selectedEdition.quote_count}</div>
                  <div className="text-sm text-gray-500">Quotes</div>
                </div>
                <div className="ml-auto text-sm text-gray-500">
                  {formatDate(selectedEdition.date)}
                </div>
              </div>

              <div className="prose prose-lg max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => <h1 className="text-2xl font-bold text-gray-900 mt-6 mb-3">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-xl font-semibold text-gray-800 mt-5 mb-2">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">{children}</h3>,
                    p: ({ children }) => <p className="text-gray-700 mb-3 leading-relaxed">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc pl-5 mb-4 space-y-1">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal pl-5 mb-4 space-y-1">{children}</ol>,
                    li: ({ children }) => <li className="text-gray-700">{children}</li>,
                    strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-purple-300 pl-4 italic text-gray-600 my-4">
                        {children}
                      </blockquote>
                    ),
                    table: ({ children }) => (
                      <table className="min-w-full border-collapse border border-gray-200 my-4 text-sm">
                        {children}
                      </table>
                    ),
                    thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
                    tbody: ({ children }) => <tbody className="divide-y divide-gray-200">{children}</tbody>,
                    tr: ({ children }) => <tr>{children}</tr>,
                    th: ({ children }) => (
                      <th className="px-4 py-2 text-left font-semibold text-gray-700 border border-gray-200">
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className="px-4 py-2 text-gray-600 border border-gray-200">
                        {children}
                      </td>
                    ),
                  }}
                >
                  {selectedEdition.summary_markdown}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
              <p>Select an edition from the list to view.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
